import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import {Transition} from 'react-transition-group';
import {StyledExpensesTabs, StyledExpensesTabsContainer} from './StyledExpensesTabs';
import ExpensesTab from '../ExpensesTab';
import ExpenseDetailsPanel from '../ExpenseDetailsPanel';
import ApproveExpenseModal from '../ExpenseDetailsPanelTabs/tabComponents/ApproveExpenseModal';
import ExpenseAgentChat from '../ExpenseAgentChat';
import RejectExpenseModal from '../ExpenseDetailsPanelTabs/tabComponents/RejectExpenseModal';
import WebSocket from '../../../WebSocket';
import {
  backendWebsocketActionsConstants,
  DEFAULT_MODAL_PROPS,
  DEFAULT_PAGE_SIZE,
  expenseReviewStatusConstants,
  SYSTEM_ANIMATION_DURATION
} from '../../../../constants';
import {useAccountingTabLogic, useTabState} from '../../../../hooks';
import {
  accountingHelpers,
  expensesHelpers,
  locationHelpers,
  objectHelpers,
  tabHelpers,
  transactionsHelpers,
} from '../../../../utils/helpers';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {EXPENSES_PAGE_SEARCH_KEYS} from '../../../../pages/ExpensesPage/expensesPage.constants';

const tabKeys = {
  REQUESTED: 'requested',
  REVIEWED: 'reviewed',
};

const {REQUESTED, REVIEWED} = tabKeys;

const defaultTabKey = tabKeys.REQUESTED;

const tabKeysArr = [REQUESTED, REVIEWED];

const {details: detailsStyles, table: tableStyles} = transactionsHelpers.getAnimationStyles({duration: SYSTEM_ANIMATION_DURATION, detailsMaxWidth: 434});

const defaultModalProps = { ...DEFAULT_MODAL_PROPS, open: false };

const summaraizeKeyName = 'isMonthGrouping';

const {SELECTED_EXPENSE_ID} = EXPENSES_PAGE_SEARCH_KEYS;

const ExpensesTabs = ({
  dispatch,
  approveExpense,
  getExpense,
  getExpenses,
  reassignExpense,
  rejectExpense,
  reopenExpense,
  unlinkExpense,
  updateExpense,
  isAdmin,
  employeeEmail,
  isOpenAgentChat,
  ...rest
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [t] = useTranslation(['main', 'expenses']);
  const [activeTabKey, setActiveTabKey] = useState(locationHelpers.getHash(location) || defaultTabKey);
  const [detailsWindowProps, setDetailsWindowProps] = useState(defaultModalProps);
  const [approveModalProps, setApproveModalProps] = useState(defaultModalProps);
  const [rejectModalProps, setRejectModalProps] = useState(defaultModalProps);
  const [enabledWebSocketConnection, setEnabledWebSocketConnection] = useState(false);

  const {
    tableData, setTableData,
    queries, setQueries,
    loading, setLoading,
    requiredUpdate, setRequiredUpdate,
  } = useTabState({defaultQuery: { order_by: '-date', per_page: DEFAULT_PAGE_SIZE }, tabKeys: tabKeysArr});

  const loadData = ({
    view,
    query = null,
    initial = false,
    skipLoading = false,
    updateRequired = true,
    updateCurrentPage = false,
    onSuccess
  }) => {
    const viewQuery = tabHelpers.getViewQuery({
      initial, query, queries, view, updateCurrentPage, tableData
    });

    setQueries({ ...queries, [view]: viewQuery});

    const finishTableLoading = () => {
      setLoading(prevState => ({...prevState, [view]: false}));
      if (updateRequired) {
        setRequiredUpdate(prevState => ({...prevState, [view]: false}));
      }
    }

    if (!skipLoading) {
      setLoading(prevState => ({...prevState, [view]: true}));
    }

    getExpenses({
      query: getQuery(viewQuery, view),
      successCallback: (response) => {
        response.results = expensesHelpers.getGroupedExpenses(response.results);

        if (!initial) {
          const combinedRows = [...tableData[view].rows, ...response.results].filter(row => !row.isMonthGrouping);
          response.results = expensesHelpers.getGroupedExpenses(combinedRows);
        }

        tabHelpers.handleStoreTableDataWithPrev({
          tableData: initial ? tableData : { ...tableData, [view]: { ...tableData[view], rows: [] } },
          setTableData,
          view,
          response,
          initial,
          updateCurrentPage
        });

        !skipLoading && finishTableLoading();
        onSuccess?.();
      },
      errorCallback: () => !skipLoading && finishTableLoading()
    });
  }

  const loadExpenseData = ({id, successCallback, errorCallback} = {}) => {
    if (!id) return;
    getExpense({
      id,
      successCallback: (expense) => {
        setTableData(prevState => ({
          ...tableData,
          [activeTabKey]: {
            ...prevState[activeTabKey],
            rows: (tableData[activeTabKey]?.rows || []).map(exp => exp.id === expense.id ? expense : exp)
          }
        }));
        successCallback?.(expense);
      },
      errorCallback
    })
  }

  const { handleOnTableChange, handleOnFilter, onChangeTab } = useAccountingTabLogic({
    tabKeys,
    tableData,
    setTableData,
    queries,
    setQueries,
    requiredUpdate,
    loadData,
    setActiveTabKey,
  });

  const handleOnTabChange = (key) => {
    onChangeTab(key);
    if (detailsWindowProps.open) setDetailsWindowProps(prevState => ({...prevState, open: false}));
  }

  const trans = (key, props) => t(`expenses:${key}`, props);

  useEffect(() => {
    if (isOpenAgentChat && !enabledWebSocketConnection) {
      setEnabledWebSocketConnection(true);
    }
  }, [isOpenAgentChat]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData({
      initial: true,
      view: tabKeys[activeTabKey.toUpperCase()]
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!!detailsWindowProps.expense) return;

    const selectedExpenseId = new URLSearchParams(location.search).get(SELECTED_EXPENSE_ID);
    if (!selectedExpenseId) return;

    const expense = tableData[activeTabKey]?.rows?.find(e => e.id === selectedExpenseId);
    if (expense) {
      handleOnRowClick(undefined, expense);
    } else {
      const loaded = tableData[activeTabKey].loaded;
      if (loaded) {
        loadExpenseData({
          id: selectedExpenseId,
          successCallback: () => handleOnRowClick(undefined, expense)
        });
      }
    }
  }, [tableData]); // eslint-disable-line react-hooks/exhaustive-deps

  const getLabel = (key) => trans(`expensesList.viewOptions.${key}`);

  const getQuery = (query, view) => {
    let statusesEnum = {
      [REQUESTED]: [expenseReviewStatusConstants.IN_REVIEW],
      [REVIEWED]: [expenseReviewStatusConstants.APPROVED, expenseReviewStatusConstants.REJECTED],
    };
    return {
      ...query,
      review_status: statusesEnum[view]
    }
  }

  const handleOnRowClick = (e, expense) => {
    if (!expense || expense[summaraizeKeyName]) return;
    setDetailsWindowProps(prevState => {
      const open = expense?.id === prevState.expense?.id ? !prevState.open : true;
      return { ...prevState, open, expense, index: open ? expense?.id : null }
    });

    const searchParams = new URLSearchParams(location.search);
    if (expense.id !== detailsWindowProps.expense?.id) {
      searchParams.set(SELECTED_EXPENSE_ID, expense?.id);
    } else {
      searchParams.delete(SELECTED_EXPENSE_ID);
    }

    navigate({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
      hash: location.hash,
    });
  }

  const handleCloseApproveModal = () => setApproveModalProps({...approveModalProps, open: false});

  const handleCloseRejectModal = () => setRejectModalProps({...rejectModalProps, open: false});

  const handleOpenRejectModal = ({e, expense, data = {}}) => {
    e?.stopPropagation();
    setRejectModalProps({
      ...defaultModalProps,
      open: true,
      expense,
      data
    });
  }

  const updateTableAndDetails = ({id, response, forceUpdate = false} = {}) => {
    const expenseFromTable = tableData[activeTabKey].rows.find(e => e.id === id);
    if (forceUpdate || (expenseFromTable && expenseFromTable?.review_status !== response?.review_status)) {
      loadData({
        view: activeTabKey,
        initial: true,
        onSuccess: () => {
          // check if another view is already loaded, if yes, then load it too
          Object.keys(tabKeys).forEach(key => {
            const tabKey = key.toLowerCase();
            if (tabKey !== activeTabKey && tableData[tabKey].loaded) {
              loadData({view: tabKey, initial: true, skipLoading: true});
            }
          });
        }
      });
    } else {
      setTableData(prevState => ({
        ...prevState,
        [activeTabKey]: {
          ...prevState[activeTabKey],
          rows: prevState[activeTabKey].rows.map(exp => exp.id === id ? response : exp)
        }
      }));
    }

    if (detailsWindowProps.open && detailsWindowProps?.expense?.id === id) {
      setDetailsWindowProps(prevState => ({ ...prevState, expense: response }));
    }
  };

  const handleOkRejectModal = ({fields, successCallback, errorCallback}) => {
    const id = rejectModalProps?.expense?.id;
    if (!id) return;

    setRejectModalProps(prevState => ({ ...prevState, loading: true }));

    rejectExpense({
      id,
      data: {
        ...fields,
        ...(rejectModalProps?.data || {})
      },
      successCallback: (response) => {
        setRejectModalProps(defaultModalProps);
        updateTableAndDetails({id, response});
        successCallback?.(response);
      },
      errorCallback: (err) => {
        setRejectModalProps(prevState => ({ ...prevState, loading: false }));
        errorCallback?.(err?.response);
      },
    });
  }

  const handleOkApproveModal = ({successCallback, errorCallback}) => {
    const expense = approveModalProps?.expense;
    if (!expense) return;

    setApproveModalProps(prevState => ({ ...prevState, loading: true }))
    handleApproveExpense({
      expense,
      data: { admin_override: true },
      successCallback: (response) => {
        handleCloseApproveModal();
        successCallback?.(response);
      },
      errorCallback: (response) => {
        setApproveModalProps(prevState => ({ ...prevState, loading: false }))
        errorCallback?.(response);
      }
    });
  }

  const handleApproveTableExpense = ({e, expense}) => {
    e?.stopPropagation();

    const canQuickApprove = expensesHelpers.canQuickApprove({expense, employeeEmail, isAdmin});
    if (canQuickApprove) {
      handleApproveExpense({e, expense});
    } else {
      setApproveModalProps({
        ...defaultModalProps,
        open: true,
        expense,
      });
    }
  }

  const handleApproveDetailsExpense = ({e, expense, data}) => {
    const action = data?.admin_override ? handleApproveTableExpense : handleApproveExpense;
    action({e, expense});
  }

  const handleApproveExpense = ({e, expense, data = {}, successCallback, errorCallback}) => {
    e?.stopPropagation();

    const id = expense?.id;
    if (!id) return;

    approveExpense({
      id,
      data,
      successCallback: (response) => {
        updateTableAndDetails({id, response});
        successCallback?.(response);
      },
      errorCallback: (response) => {
        expensesHelpers.handleExpenseError({response, fallbackKey: 'approveExpenseError', t});
        errorCallback?.(response);
      },
    });
  }

  const handleReviewEdit = () => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    reopenExpense({
      id,
      successCallback: (response) => updateTableAndDetails({id, response, forceUpdate: true}),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'reopenExpenseError', t}),
    });
  }

  const handleReassignExpense = (e, data) => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    reassignExpense({
      id,
      data,
      successCallback: (response) => updateTableAndDetails({id, response}),
      errorCallback: (response) =>expensesHelpers.handleExpenseError({response, fallbackKey: 'reassignExpenseError', t}),
    });
  }

  const handleEditExpense = (data) => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    updateExpense({
      id,
      data,
      successCallback: () => {
        setTableData(prevState => ({
          ...prevState,
          [activeTabKey]: {
            ...prevState[activeTabKey],
            rows: prevState[activeTabKey].rows.map(exp => exp.id === id ? {...exp, ...data} : exp)
          }
        }));

        if (detailsWindowProps.open && detailsWindowProps?.expense?.id === id) {
          setDetailsWindowProps(prevState => ({ ...prevState, expense: { ...prevState.expense, ...data } }));
        }
      },
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'updateExpenseError', t}),
    })
  }

  const handleUpdateExpense = (updatedExpense) => {
    const selectedExpense = detailsWindowProps?.expense;
    const id = selectedExpense?.id;
    if (!id) return;

    if (updatedExpense) {
      if (detailsWindowProps.open && detailsWindowProps?.expense?.id === id) {
        setDetailsWindowProps(prevState => ({ ...prevState, expense: { ...prevState.expense, ...updatedExpense } }));
      }

      const updateExpensesList = () => {
        setTableData(prevState => ({
          ...prevState,
          [activeTabKey]: {
            ...prevState[activeTabKey],
            rows: prevState[activeTabKey].rows.map(exp => exp.id === id ? {...exp, ...updatedExpense} : exp)
          }
        }));
      }

      // remove or add expense to table if unmatched filter is selected
      if (
        String(queries[activeTabKey]?.has_invoice) === 'false' &&
        selectedExpense.expense_type !== updatedExpense.expense_type
      ) {
        const isExpenseTypeAdded = selectedExpense.expense_type === null && updatedExpense.expense_type !== null;
        const isExpenseTypeRemoved = selectedExpense.expense_type !== null && updatedExpense.expense_type === null;
        if (isExpenseTypeAdded) {
          setTableData(prevState => ({
            ...prevState,
            [activeTabKey]: {
              ...prevState[activeTabKey],
              rows: prevState[activeTabKey].rows.filter(i => i.id !== id)
            }
          }));
          return;
        } else if (isExpenseTypeRemoved) {
          updateExpensesList();
          return;
        }
      }
      updateExpensesList();
    }
  }

  const handleInvoiceRemove = (successCallback, errorCallback) => {
    transactionsHelpers.removeInvoiceFromTransaction({
      t,
      transaction: {expense: detailsWindowProps?.expense},
      successCallback,
      errorCallback,
      unlinkExpense,
      onExpenseUpdate: handleUpdateExpense
    });
  }

  const handleWebsocketMessage = (data) => {
    const getResponseProp = (key) => objectHelpers.getObjProp(data, key);
    const action = getResponseProp('action');

    if (!action) return;

    ({
      [backendWebsocketActionsConstants.EXPENSE_SUBMITTED]: () => {
        if (tableData[defaultTabKey].loaded) {
          loadData({
            initial: true,
            view: defaultTabKey
          });
        }
      },
      [backendWebsocketActionsConstants.EXPENSE_FILL_MISSING_INFO]: () => {
        const expenseId = getResponseProp('expense_id') || getResponseProp('expense');
        if (!expenseId) return;

        loadExpenseData({
          id: expenseId,
          successCallback: (expense) => {
            if (detailsWindowProps.open && detailsWindowProps?.expense?.id === expenseId) {
              setDetailsWindowProps({...detailsWindowProps, expense});
            }
          }
        });
      }
    })[action]?.();
  };

  const handleCloseDetailsModal = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(SELECTED_EXPENSE_ID);

    navigate({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
      hash: location.hash,
    });

    setDetailsWindowProps({...detailsWindowProps, open: false, expense: null, index: null});
  }

  const getLoadMoreProps = (view) => {
    const cursor = tableData[view]?.pagination?.cursor;
    return {
      cursor: cursor,
      isEnabled: Boolean(cursor),
      onClick: () => loadData({view, query: { cursor }})
    }
  }

  const getTableFiltersProps = (view) => ({
    exportAdditionalFields: getQuery(null, view), // to include current filters in export
    filterFields: [
      'search',
      ...(isAdmin ? ['reported_by', 'approver'] : []),
      ...(view === REQUESTED ? ['status'] : [])
    ],
  });

  const getTableProps = (view) => ({
    onChange: (pagination, filters, sorter, extra) => handleOnTableChange(view, pagination, filters, sorter, extra),
    onRow: (record) => ({
      onClick: (e) => handleOnRowClick(e, record)
    }),
    pagination: false,
    rowClassName: (record) => transactionsHelpers.getRowClassName({
      groupedPropName: summaraizeKeyName,
      index: record?.id,
      record,
      selectedRowIndex: detailsWindowProps?.index,
    }),
  });

  const getTabProps = (key) => ({
    data: accountingHelpers.getTableDataValue(tableData, key, 'rows', []),
    tableFiltersProps: getTableFiltersProps(key),
    loadMoreProps: getLoadMoreProps(key),
    loading: loading[key] || false,
    onApprove: handleApproveTableExpense,
    onReject: handleOpenRejectModal,
    onFilter: (value) => handleOnFilter(key, value),
    tableProps: getTableProps(key),
  });

  const tabs = tabKeysArr.map((key) => ({
    key,
    label: getLabel(key),
    children: <ExpensesTab {...getTabProps(key)} />
  }));

  return (
    <StyledExpensesTabsContainer>
      <Transition in={detailsWindowProps.open} timeout={SYSTEM_ANIMATION_DURATION}>
        {state => (
          <StyledExpensesTabs
            {...rest}
            activeKey={activeTabKey}
            items={tabs}
            onChange={handleOnTabChange}
            type='card'
            style={{ ...tableStyles.default, ...tableStyles[state] }}
          />
        )}
      </Transition>
      <Transition in={detailsWindowProps.open} timeout={SYSTEM_ANIMATION_DURATION}>
        {state => (
          <ExpenseDetailsPanel
            {...detailsWindowProps}
            className='expense-details-panel'
            onApprove={handleApproveDetailsExpense}
            onClose={handleCloseDetailsModal}
            onEdit={handleEditExpense}
            onInvoiceRemove={handleInvoiceRemove}
            onUpdate={handleUpdateExpense}
            onReassign={handleReassignExpense}
            onReject={({e, data = {}}) => handleOpenRejectModal({data, e, expense: detailsWindowProps.expense})}
            onReviewEdit={handleReviewEdit}
            style={{ ...detailsStyles.default, ...detailsStyles[state] }}
          />
        )}
      </Transition>

      <RejectExpenseModal
        {...rejectModalProps}
        handleCancel={handleCloseRejectModal}
        handleOk={handleOkRejectModal}
      />

      <ApproveExpenseModal
        {...approveModalProps}
        handleCancel={handleCloseApproveModal}
        handleOk={handleOkApproveModal}
      />

      <ExpenseAgentChat />

      {enabledWebSocketConnection && <WebSocket onMessage={handleWebsocketMessage} />}

    </StyledExpensesTabsContainer>
  );
}

const mapStateToProps = ({
  expense: { agentChatProps },
  user: { isAdmin, employee }
}) => ({  isAdmin,
  isOpenAgentChat: agentChatProps.open,
  employeeEmail: employee?.email,
});

const mapDispatchToProps = {
  approveExpense: expenseActions.approveExpense,
  getExpenses: expenseActions.getExpenses,
  getExpense: expenseActions.getExpense,
  reassignExpense: expenseActions.reassignExpense,
  rejectExpense: expenseActions.rejectExpense,
  reopenExpense: expenseActions.reopenExpense,
  unlinkExpense: invoicesActions.unlinkExpense,
  updateExpense: expenseActions.updateExpense,
}

export default connect(mapStateToProps, mapDispatchToProps)(ExpensesTabs);
